import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, peso } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Ticket, Users, Megaphone, TrendingUp, Plus, X,
  Play, Square, LockKeyhole, Check, QrCode, TriangleAlert, Trash2,
} from "lucide-react";

const STATUS_COLORS = {
  draft:     "text-foreground/50 border-foreground/20",
  published: "text-emerald-500 border-emerald-500/40",
  live:      "text-red-500 border-red-500/40 animate-pulse",
  closed:    "text-foreground/40 border-foreground/15",
  cancelled: "text-red-500 border-red-500/40 opacity-60",
};

const TABS = [
  { key: "overview",    label: "Overview",    icon: Ticket },
  { key: "tickets",     label: "Tickets",     icon: QrCode },
  { key: "guests",      label: "Guest list",  icon: Users },
  { key: "commissions", label: "Commissions", icon: TrendingUp },
];

export default function EventDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { hasPerm } = useAuth();

  const [event, setEvent] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [guests, setGuests] = useState([]);
  const [commissions, setCommissions] = useState(null);
  const [promoters, setPromoters] = useState([]);
  const [tab, setTab] = useState("overview");
  const [loading, setLoading] = useState(true);

  const [addingType, setAddingType] = useState(false);
  const [selling, setSelling] = useState(false);
  const [addingGuest, setAddingGuest] = useState(false);
  const [statusDialog, setStatusDialog] = useState(null);

  const load = async () => {
    try {
      const [e, d, p] = await Promise.all([
        api.get(`/events/${id}`),
        api.get(`/events/${id}/dashboard`),
        promoters.length ? Promise.resolve({ data: promoters }) : api.get("/promoters", { params: { active: true } }),
      ]);
      setEvent(e.data); setDashboard(d.data);
      if (!promoters.length) setPromoters(p.data);
    } catch (_) { toast.error("Could not load event"); }
    finally { setLoading(false); }
  };
  const loadTab = async () => {
    if (!event) return;
    if (tab === "tickets") {
      const { data } = await api.get(`/events/${id}/tickets`);
      setTickets(data);
    } else if (tab === "guests") {
      const { data } = await api.get(`/events/${id}/guest-list`);
      setGuests(data);
    } else if (tab === "commissions") {
      const { data } = await api.get(`/events/${id}/commissions`);
      setCommissions(data);
    }
  };
  useEffect(() => { load(); }, [id]);
  useEffect(() => { loadTab(); }, [tab, event?.id]);

  if (loading) return <div className="p-14 ink-subtle text-sm">Loading event…</div>;
  if (!event)  return <div className="p-14 ink-subtle text-sm">Event not found.</div>;

  const closed = event.status === "closed" || event.status === "cancelled";

  return (
    <div className="p-8 lg:p-14 min-h-full" data-testid="event-detail-page">
      {/* Header */}
      <button onClick={() => nav("/events")} className="tiny-label ink-subtle hover:ink flex items-center gap-1" data-testid="event-back-btn">
        <ArrowLeft size={12}/> All events
      </button>
      <div className="mt-6 flex flex-wrap items-start justify-between gap-8 animate-fade-up">
        <div className="min-w-0">
          <div className="flex items-center gap-3">
            <span className={`text-[9px] uppercase tracking-[0.24em] font-bold px-2 py-0.5 rounded-full border ${STATUS_COLORS[event.status]}`} data-testid="event-status">
              {event.status}
            </span>
            <span className="tiny-label ink-subtle mono">
              {new Date(event.starts_at).toLocaleString("en-PH", { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
              {" → "}
              {new Date(event.ends_at).toLocaleTimeString("en-PH", { hour: "2-digit", minute: "2-digit" })}
            </span>
          </div>
          <h1 className="mt-4 display-heading text-4xl md:text-5xl ink">{event.name}</h1>
          {event.description && <p className="mt-3 ink-muted text-sm max-w-lg leading-relaxed">{event.description}</p>}
        </div>
        {hasPerm("event.manage") && (
          <div className="flex flex-wrap items-center gap-2">
            {event.status === "draft" && <StatusBtn label="Publish" icon={Play} onClick={() => setStatusDialog("published")}/>}
            {event.status === "published" && <StatusBtn label="Go live" icon={Play} onClick={() => setStatusDialog("live")}/>}
            {(event.status === "live" || event.status === "published") && <StatusBtn label="Close event" icon={LockKeyhole} solid onClick={() => setStatusDialog("closed")}/>}
            {event.status !== "closed" && event.status !== "cancelled" && (
              <StatusBtn label="Cancel" icon={Square} destructive onClick={() => setStatusDialog("cancelled")}/>
            )}
          </div>
        )}
      </div>

      {/* KPI strip */}
      <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-x-12 gap-y-6 animate-fade-up animate-fade-up-1">
        <Kpi num=".01" label="Tickets sold"  value={dashboard.tickets_sold} sub={`of ${event.capacity || "∞"} · ${dashboard.capacity_used_pct}%`}/>
        <Kpi num=".02" label="Checked in"    value={dashboard.tickets_used}/>
        <Kpi num=".03" label="Ticket revenue" value={peso(dashboard.ticket_revenue)} accent/>
        <Kpi num=".04" label="Guests arriving" value={dashboard.guests_arrived_heads} sub={`of ${dashboard.guests_expected_heads} expected`}/>
      </div>

      {/* Tabs */}
      <div className="mt-14 border-b hairline-soft animate-fade-up animate-fade-up-2">
        <div className="flex gap-6 -mb-px">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)}
                    data-testid={`event-tab-${t.key}`}
                    className={`pb-4 flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] font-bold transition-colors border-b-2 ${
                      tab === t.key ? "border-foreground ink" : "border-transparent text-foreground/40 hover:text-foreground/70"
                    }`}>
              <t.icon size={12}/> {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div className="mt-10 animate-fade-up animate-fade-up-3">
        {tab === "overview" && (
          <OverviewTab event={event} dashboard={dashboard}
                       onAddType={() => setAddingType(true)}
                       canManage={hasPerm("event.manage") && !closed}
                       reload={load}/>
        )}
        {tab === "tickets" && (
          <TicketsTab tickets={tickets} event={event} onSell={() => setSelling(true)} canManage={hasPerm("event.manage") && !closed} reload={loadTab}/>
        )}
        {tab === "guests" && (
          <GuestsTab guests={guests} promoters={promoters} onAdd={() => setAddingGuest(true)} canManage={hasPerm("guest_list.manage") && !closed} reload={loadTab}/>
        )}
        {tab === "commissions" && commissions && (
          <CommissionsTab commissions={commissions}/>
        )}
      </div>

      {/* Drawers */}
      {addingType && <TicketTypeDrawer event={event} onClose={() => { setAddingType(false); load(); }}/>}
      {selling && <SellDrawer event={event} promoters={promoters} onClose={() => { setSelling(false); load(); loadTab(); }}/>}
      {addingGuest && <GuestDrawer event={event} promoters={promoters} onClose={() => { setAddingGuest(false); loadTab(); load(); }}/>}
      {statusDialog && (
        <StatusChangeDialog event={event} status={statusDialog}
                             onCancel={() => setStatusDialog(null)}
                             onConfirm={async () => {
                               try {
                                 await api.post(`/events/${event.id}/status?status=${statusDialog}`);
                                 toast.success(`Event ${statusDialog}`);
                                 setStatusDialog(null); load();
                               } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
                             }}/>
      )}
    </div>
  );
}

/* ============ Sub-components ============ */

function Kpi({ num, label, value, accent, sub }) {
  return (
    <div data-testid={`event-kpi-${label.toLowerCase().replace(/[^\w]+/g,"-")}`}>
      <div className="flex items-center justify-between">
        <div className="section-eyebrow">{label}</div>
        <span className="section-num">{num}</span>
      </div>
      <div className={`mt-3 mono ink text-2xl font-semibold ${accent ? "border-l-2 border-foreground pl-3" : ""}`}>{value}</div>
      {sub && <div className="mt-1 tiny-label ink-subtle">{sub}</div>}
    </div>
  );
}

function StatusBtn({ label, icon: Icon, onClick, solid, destructive }) {
  return (
    <button onClick={onClick}
            data-testid={`event-status-${label.toLowerCase().replace(/[^\w]+/g,"-")}`}
            className={`h-10 px-4 rounded-full border text-[10px] uppercase tracking-[0.24em] font-bold flex items-center gap-2 transition-colors ${
              solid ? "bg-foreground text-background border-foreground"
                    : destructive ? "border-red-500/50 text-red-500 hover:bg-red-500/5"
                                  : "hairline text-foreground/70 hover:text-foreground hover:border-foreground/50"
            }`}>
      <Icon size={12}/> {label}
    </button>
  );
}

function OverviewTab({ event, dashboard, onAddType, canManage, reload }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
      <div className="lg:col-span-2">
        <div className="section-eyebrow flex items-center justify-between mb-4">
          <span>Ticket types</span>
          {canManage && (
            <button onClick={onAddType} data-testid="event-add-type-btn" className="btn-pill btn-pill-outline h-8 text-[10px]"><Plus size={12}/> Add type</button>
          )}
        </div>
        {(event.ticket_types || []).length === 0 && (
          <div className="border hairline-soft rounded-md p-10 text-center ink-subtle text-sm">
            No ticket types yet. Add at least one before publishing.
          </div>
        )}
        <div className="divide-y hairline-soft border-y hairline-soft" data-testid="event-type-list">
          {(event.ticket_types || []).map((t) => {
            const capPct = t.capacity ? Math.round((t.sold / t.capacity) * 100) : 0;
            return (
              <div key={t.id} className="py-4 flex items-center gap-4" data-testid={`event-type-${t.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] uppercase tracking-[0.24em] font-semibold ink truncate">{t.name}</span>
                    {!t.active && <span className="text-[9px] uppercase tracking-[0.22em] font-bold text-red-500">OFF</span>}
                    {!t.allow_promoter && <span className="text-[9px] uppercase tracking-[0.22em] font-bold text-foreground/50">no promo</span>}
                  </div>
                  <div className="mt-1 tiny-label ink-subtle">
                    <span className="mono ink">{t.sold}</span> sold · <span className="mono ink">{t.remaining}</span> remaining · cap {t.capacity || "∞"}
                  </div>
                  {t.capacity > 0 && (
                    <div className="mt-2 h-1 rounded-full bg-foreground/10 overflow-hidden">
                      <div className={`h-full ${capPct > 90 ? "bg-red-500" : capPct > 60 ? "bg-amber-500" : "bg-foreground"}`}
                           style={{ width: `${Math.min(capPct, 100)}%` }}/>
                    </div>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <div className="mono ink text-lg font-semibold">{peso(t.price)}</div>
                  <div className="tiny-label ink-subtle">per ticket</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="space-y-8">
        <div>
          <div className="section-eyebrow mb-4">Facts</div>
          <div className="border hairline-soft rounded-md divide-y hairline-soft">
            <Fact k="Capacity" v={event.capacity || "Unlimited"}/>
            <Fact k="Promoter %" v={`${event.promoter_commission_pct || 0}%`}/>
            <Fact k="Per-arrived guest" v={peso(event.guest_commission_flat || 0)}/>
            {event.closed_at && <Fact k="Closed" v={new Date(event.closed_at).toLocaleString("en-PH", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}/>}
          </div>
        </div>
      </div>
    </div>
  );
}

function Fact({ k, v }) {
  return (
    <div className="px-4 py-3 flex items-center justify-between">
      <div className="tiny-label ink-subtle">{k}</div>
      <div className="mono ink text-sm">{v}</div>
    </div>
  );
}

function TicketsTab({ tickets, event, onSell, canManage, reload }) {
  const [statusFilter, setStatusFilter] = useState("all");
  const filtered = statusFilter === "all" ? tickets : tickets.filter((t) => t.status === statusFilter);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          {["all","valid","used","void"].map((s) => (
            <button key={s} onClick={() => setStatusFilter(s)}
                    data-testid={`event-ticket-filter-${s}`}
                    className={`h-8 px-3 rounded-full text-[10px] uppercase tracking-[0.24em] font-bold border transition-colors ${
                      statusFilter === s ? "bg-foreground text-background border-foreground" : "hairline text-foreground/60 hover:text-foreground"
                    }`}>{s} <span className="mono opacity-70">{s === "all" ? tickets.length : tickets.filter((t)=>t.status===s).length}</span></button>
          ))}
        </div>
        {canManage && (
          <button onClick={onSell} data-testid="event-sell-btn" className="btn-pill btn-pill-solid h-10"><Plus size={12}/> Sell ticket</button>
        )}
      </div>

      <div className="border-y hairline-soft divide-y hairline-soft" data-testid="event-ticket-list">
        <div className="grid grid-cols-12 py-3 tiny-label ink-subtle">
          <div className="col-span-3">Code</div>
          <div className="col-span-3">Holder</div>
          <div className="col-span-2">Type</div>
          <div className="col-span-2">Promoter</div>
          <div className="col-span-1 text-right">Paid</div>
          <div className="col-span-1 text-right">Status</div>
        </div>
        {filtered.length === 0 && <div className="py-8 ink-subtle text-center text-sm">No tickets in this bucket.</div>}
        {filtered.map((t) => (
          <div key={t.id} className="grid grid-cols-12 py-3 items-center" data-testid={`event-ticket-${t.id}`}>
            <div className="col-span-3 mono text-sm ink font-semibold">{t.code}</div>
            <div className="col-span-3 pr-2 min-w-0">
              <div className="text-[11px] uppercase tracking-[0.24em] font-semibold ink truncate">{t.holder_name}</div>
              <div className="text-xs ink-subtle mono truncate">{t.holder_email || t.holder_phone || "—"}</div>
            </div>
            <div className="col-span-2 tiny-label truncate pr-2">{t.type_name}</div>
            <div className="col-span-2 tiny-label ink-subtle truncate pr-2">{t.promoter_name || "—"}</div>
            <div className="col-span-1 text-right mono text-sm ink">{peso(t.price_paid)}</div>
            <div className="col-span-1 text-right">
              <span className={`text-[9px] uppercase tracking-[0.22em] font-bold px-2 py-0.5 rounded-full border ${
                t.status === "used" ? "text-emerald-500 border-emerald-500/40"
                  : t.status === "void" ? "text-red-500 border-red-500/40"
                    : "text-foreground/60 border-foreground/20"
              }`}>{t.status}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function GuestsTab({ guests, promoters, onAdd, canManage, reload }) {
  const [listFilter, setListFilter] = useState("all");
  const lists = [...new Set(guests.map((g) => g.list_name))];
  const filtered = listFilter === "all" ? guests : guests.filter((g) => g.list_name === listFilter);

  const { hasPerm } = useAuth();
  const canManualArrive = hasPerm("entrance.override");

  const manualArrival = async (g) => {
    // Phase 1 closure §4 — a normal check-in should go through the entrance
    // scanner. This is a MANUAL correction path only, requires a reason.
    const reason = window.prompt(`Manual arrival correction for ${g.name}\n\nGuest admissions should go through the entrance scanner. This override should only be used to fix a data error.\n\nReason (required):`);
    if (!reason || !reason.trim()) return;
    try {
      await api.patch(`/guest-list-entries/${g.id}`,
                       { arrival_status: "arrived", notes: reason.trim() });
      toast.success(`${g.name} — manual arrival recorded`);
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const markNoShow = async (g) => {
    try {
      await api.patch(`/guest-list-entries/${g.id}`, { arrival_status: "no_show" });
      toast.success(`${g.name} — no-show`);
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (g) => {
    if (!window.confirm(`Remove ${g.name} from the guest list?`)) return;
    try {
      await api.delete(`/guest-list-entries/${g.id}`);
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setListFilter("all")}
                  data-testid="event-guest-filter-all"
                  className={`h-8 px-3 rounded-full text-[10px] uppercase tracking-[0.24em] font-bold border transition-colors ${
                    listFilter === "all" ? "bg-foreground text-background border-foreground" : "hairline text-foreground/60 hover:text-foreground"
                  }`}>All <span className="mono opacity-70">{guests.length}</span></button>
          {lists.map((l) => (
            <button key={l} onClick={() => setListFilter(l)}
                    data-testid={`event-guest-filter-${l}`}
                    className={`h-8 px-3 rounded-full text-[10px] uppercase tracking-[0.24em] font-bold border transition-colors ${
                      listFilter === l ? "bg-foreground text-background border-foreground" : "hairline text-foreground/60 hover:text-foreground"
                    }`}>{l} <span className="mono opacity-70">{guests.filter((g)=>g.list_name===l).length}</span></button>
          ))}
        </div>
        {canManage && (
          <button onClick={onAdd} data-testid="event-guest-add-btn" className="btn-pill btn-pill-solid h-10"><Plus size={12}/> Add to guest list</button>
        )}
      </div>

      <div className="border-y hairline-soft divide-y hairline-soft" data-testid="event-guest-list">
        <div className="grid grid-cols-12 py-3 tiny-label ink-subtle">
          <div className="col-span-3">Name</div>
          <div className="col-span-2">List</div>
          <div className="col-span-1 text-right">+</div>
          <div className="col-span-2">Promoter</div>
          <div className="col-span-2">Notes</div>
          <div className="col-span-2 text-right">Arrival</div>
        </div>
        {filtered.length === 0 && <div className="py-8 ink-subtle text-center text-sm">No guests on this list yet.</div>}
        {filtered.map((g) => (
          <div key={g.id} className="grid grid-cols-12 py-3 items-center" data-testid={`event-guest-${g.id}`}>
            <div className="col-span-3 text-[11px] uppercase tracking-[0.24em] font-semibold ink truncate">{g.name}</div>
            <div className="col-span-2 tiny-label">{g.list_name}</div>
            <div className="col-span-1 text-right mono text-sm ink">+{g.plus_ones || 0}</div>
            <div className="col-span-2 tiny-label ink-subtle truncate">{g.promoter_name || "—"}</div>
            <div className="col-span-2 text-xs ink-subtle italic truncate">{g.notes || "—"}</div>
            <div className="col-span-2 text-right flex items-center justify-end gap-1">
              {g.arrival_status === "arrived" ? (
                <span className="text-[9px] uppercase tracking-[0.22em] font-bold text-emerald-500 border border-emerald-500/40 px-2 py-0.5 rounded-full"
                       title={g.manual_correction ? `Manual correction: ${g.manual_correction_reason}` : "Arrived via entrance scanner"}>
                  {g.manual_correction ? "Manual arrival" : "Arrived"}
                </span>
              ) : g.arrival_status === "no_show" ? (
                <span className="text-[9px] uppercase tracking-[0.22em] font-bold text-red-500 border border-red-500/40 px-2 py-0.5 rounded-full">No-show</span>
              ) : canManage ? (
                <>
                  {canManualArrive && (
                    <button onClick={() => manualArrival(g)} data-testid={`event-guest-manual-arrive-${g.id}`}
                            title="Manual arrival correction — real admissions go through the entrance scanner"
                            className="text-[10px] uppercase tracking-[0.22em] font-bold px-2 h-7 rounded-full border border-amber-500/40 text-amber-600 hover:bg-amber-500/10 transition-colors">
                      Manual arrival
                    </button>
                  )}
                  <button onClick={() => markNoShow(g)} data-testid={`event-guest-noshow-${g.id}`}
                          className="text-[10px] uppercase tracking-[0.22em] font-bold px-2 h-7 rounded-full border border-foreground/30 hover:bg-foreground hover:text-background transition-colors">
                    No-show
                  </button>
                  <button onClick={() => remove(g)} data-testid={`event-guest-remove-${g.id}`} className="ink-subtle hover:text-red-500 p-1">
                    <Trash2 size={12}/>
                  </button>
                </>
              ) : (
                <span className="text-[9px] uppercase tracking-[0.22em] font-bold text-foreground/50">Pending</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CommissionsTab({ commissions }) {
  return (
    <div>
      <div className="grid grid-cols-3 gap-8 mb-8">
        <Kpi num=".01" label="Ticket commission" value={peso(commissions.totals.commission_ticket)}/>
        <Kpi num=".02" label="Guest commission" value={peso(commissions.totals.commission_guest)}/>
        <Kpi num=".03" label="Total payable" value={peso(commissions.totals.commission_total)} accent/>
      </div>
      {commissions.snapshot && (
        <div className="mb-4 tiny-label ink-subtle">Snapshotted at {new Date(commissions.computed_at).toLocaleString("en-PH")}</div>
      )}

      <div className="border-y hairline-soft divide-y hairline-soft" data-testid="event-commission-list">
        <div className="grid grid-cols-12 py-3 tiny-label ink-subtle">
          <div className="col-span-3">Promoter</div>
          <div className="col-span-1 text-right">Rate</div>
          <div className="col-span-2 text-right">Tickets</div>
          <div className="col-span-2 text-right">Revenue</div>
          <div className="col-span-1 text-right">Guests</div>
          <div className="col-span-1 text-right">Ticket ₱</div>
          <div className="col-span-1 text-right">Guest ₱</div>
          <div className="col-span-1 text-right">Total</div>
        </div>
        {(commissions.rows || []).length === 0 && (
          <div className="py-8 ink-subtle text-center text-sm">No promoter-attributed sales yet.</div>
        )}
        {(commissions.rows || []).map((r) => (
          <div key={r.promoter_id} className="grid grid-cols-12 py-3 items-center" data-testid={`event-commission-${r.promoter_id}`}>
            <div className="col-span-3 text-[11px] uppercase tracking-[0.24em] font-semibold ink truncate">{r.promoter_name}</div>
            <div className="col-span-1 text-right mono text-sm ink">{r.rate_pct}%</div>
            <div className="col-span-2 text-right mono text-sm ink">{r.tickets_sold}</div>
            <div className="col-span-2 text-right mono text-sm ink">{peso(r.ticket_revenue)}</div>
            <div className="col-span-1 text-right mono text-sm ink">{r.guests_arrived_heads}</div>
            <div className="col-span-1 text-right mono text-sm ink">{peso(r.commission_ticket)}</div>
            <div className="col-span-1 text-right mono text-sm ink">{peso(r.commission_guest)}</div>
            <div className="col-span-1 text-right mono text-sm ink font-semibold">{peso(r.commission_total)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============ Drawers ============ */

function TicketTypeDrawer({ event, onClose }) {
  const [form, setForm] = useState({ name: "", price: 0, capacity: 0, allow_promoter: true });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/events/${event.id}/ticket-types`, {
        name: form.name, price: parseFloat(form.price) || 0,
        capacity: parseInt(form.capacity) || 0, allow_promoter: form.allow_promoter,
      });
      toast.success("Ticket type added");
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Drawer title="New ticket type" eyebrow="Events" onClose={onClose} testid="event-type-drawer">
      <form onSubmit={submit}>
        <F label="Name"><input required autoFocus value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} className="i" data-testid="event-type-form-name" placeholder="GA / VIP / Table"/></F>
        <div className="grid grid-cols-2 gap-3">
          <F label="Price (₱)"><input required type="number" step="1" value={form.price} onChange={(e)=>setForm({...form, price: e.target.value})} className="i mono" data-testid="event-type-form-price"/></F>
          <F label="Capacity"><input type="number" value={form.capacity} onChange={(e)=>setForm({...form, capacity: e.target.value})} className="i mono" data-testid="event-type-form-cap" placeholder="0 = unlimited"/></F>
        </div>
        <label className="mt-4 flex items-center gap-2 text-xs ink">
          <input type="checkbox" checked={form.allow_promoter} onChange={(e)=>setForm({...form, allow_promoter: e.target.checked})} data-testid="event-type-form-promo"/>
          Allow promoter attribution on this type
        </label>
        <SubmitRow onClose={onClose} label="Add type" testid="event-type-form-submit"/>
      </form>
    </Drawer>
  );
}

function SellDrawer({ event, promoters, onClose }) {
  const activeTypes = (event.ticket_types || []).filter((t) => t.active && t.remaining !== 0);
  const [form, setForm] = useState({
    type_id: activeTypes[0]?.id || "", holder_name: "", holder_email: "", holder_phone: "",
    promoter_id: "", price_override: "", notes: "",
  });
  const selType = event.ticket_types?.find((t) => t.id === form.type_id);
  const submit = async (e) => {
    e.preventDefault();
    try {
      const body = { ...form };
      if (!body.holder_email) delete body.holder_email;
      if (!body.promoter_id) delete body.promoter_id;
      body.price_override = body.price_override ? parseFloat(body.price_override) : null;
      const { data } = await api.post(`/events/${event.id}/tickets`, body);
      toast.success(`Sold ${data.code} — ${data.holder_name}`);
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Drawer title="Sell ticket" eyebrow="Events · Point of sale" onClose={onClose} testid="event-sell-drawer">
      <form onSubmit={submit}>
        <F label="Ticket type">
          <select required value={form.type_id} onChange={(e) => setForm({ ...form, type_id: e.target.value })} className="i" data-testid="event-sell-form-type">
            {activeTypes.length === 0 && <option value="">— No ticket types available —</option>}
            {activeTypes.map((t) => (
              <option key={t.id} value={t.id}>{`${t.name} · ₱${t.price} · ${t.remaining} left`}</option>
            ))}
          </select>
        </F>
        <F label="Holder name"><input required value={form.holder_name} onChange={(e)=>setForm({...form, holder_name: e.target.value})} className="i" data-testid="event-sell-form-name"/></F>
        <div className="grid grid-cols-2 gap-3">
          <F label="Email"><input type="email" value={form.holder_email} onChange={(e)=>setForm({...form, holder_email: e.target.value})} className="i" data-testid="event-sell-form-email"/></F>
          <F label="Phone"><input value={form.holder_phone} onChange={(e)=>setForm({...form, holder_phone: e.target.value})} className="i" data-testid="event-sell-form-phone"/></F>
        </div>
        {selType?.allow_promoter && (
          <F label="Promoter (optional)">
            <select value={form.promoter_id} onChange={(e)=>setForm({...form, promoter_id: e.target.value})} className="i" data-testid="event-sell-form-promoter">
              <option value="">— No promoter —</option>
              {promoters.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.code} · {p.commission_rate_pct}%)</option>)}
            </select>
          </F>
        )}
        <F label="Price override (₱) — leave blank to charge the type's price"><input type="number" step="0.01" value={form.price_override} onChange={(e)=>setForm({...form, price_override: e.target.value})} className="i mono" data-testid="event-sell-form-price"/></F>
        <F label="Notes"><input value={form.notes} onChange={(e)=>setForm({...form, notes: e.target.value})} className="i" data-testid="event-sell-form-notes"/></F>
        <SubmitRow onClose={onClose} label="Sell ticket" testid="event-sell-form-submit" disabled={!form.type_id}/>
      </form>
    </Drawer>
  );
}

function GuestDrawer({ event, promoters, onClose }) {
  const [form, setForm] = useState({ name: "", list_name: "GA", plus_ones: 0, promoter_id: "", notes: "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const body = { ...form, plus_ones: parseInt(form.plus_ones) || 0 };
      if (!body.promoter_id) delete body.promoter_id;
      await api.post(`/events/${event.id}/guest-list`, body);
      toast.success("Added to guest list");
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Drawer title="Add to guest list" eyebrow="Events · Guest list" onClose={onClose} testid="event-guest-drawer">
      <form onSubmit={submit}>
        <F label="Guest name"><input required autoFocus value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} className="i" data-testid="event-guest-form-name"/></F>
        <div className="grid grid-cols-2 gap-3">
          <F label="List"><input value={form.list_name} onChange={(e)=>setForm({...form, list_name: e.target.value})} className="i" data-testid="event-guest-form-list" placeholder="GA / VIP / Press"/></F>
          <F label="Plus-ones"><input type="number" min="0" value={form.plus_ones} onChange={(e)=>setForm({...form, plus_ones: e.target.value})} className="i mono" data-testid="event-guest-form-plus"/></F>
        </div>
        <F label="Promoter (optional)">
          <select value={form.promoter_id} onChange={(e)=>setForm({...form, promoter_id: e.target.value})} className="i" data-testid="event-guest-form-promoter">
            <option value="">— No promoter —</option>
            {promoters.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </F>
        <F label="Notes"><input value={form.notes} onChange={(e)=>setForm({...form, notes: e.target.value})} className="i" data-testid="event-guest-form-notes"/></F>
        <SubmitRow onClose={onClose} label="Add guest" testid="event-guest-form-submit"/>
      </form>
    </Drawer>
  );
}

function StatusChangeDialog({ event, status, onCancel, onConfirm }) {
  const messages = {
    published: "Publishing makes the event visible for ticketing. You can still edit details.",
    live: "Going live signals the door team that scans should now count. Use this on the night itself.",
    closed: "Closing snapshots the commissions and locks the event forever. This cannot be undone.",
    cancelled: "Cancelling the event stops all sales. Existing tickets remain valid until you void them.",
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="event-status-dialog">
      <div className="absolute inset-0 bg-black/70" onClick={onCancel}/>
      <div className="relative w-full max-w-md surface-2 border hairline rounded-lg p-6 shadow-2xl">
        <div className="section-eyebrow">Change status</div>
        <h2 className="mt-2 display-heading text-2xl ink">Set to <span className="mono">{status}</span>?</h2>
        <p className="mt-3 ink-muted text-sm leading-relaxed">{messages[status]}</p>
        <div className="mt-6 flex gap-2">
          <button onClick={onCancel} className="btn-pill btn-pill-outline h-11 flex-1" data-testid="event-status-cancel">Not yet</button>
          <button onClick={onConfirm} className="btn-pill btn-pill-solid h-11 flex-1" data-testid="event-status-confirm">Confirm</button>
        </div>
      </div>
    </div>
  );
}

/* ============ Reusable primitives ============ */

function Drawer({ title, eyebrow, onClose, testid, children }) {
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid={testid}>
      <div className="absolute inset-0 bg-black/70" onClick={onClose}/>
      <div className="relative w-full max-w-md surface-2 border-l hairline h-full p-6 overflow-y-auto shadow-2xl">
        <div className="flex items-start justify-between">
          <div>
            <div className="section-eyebrow">{eyebrow}</div>
            <h2 className="mt-2 display-heading text-2xl ink">{title}</h2>
          </div>
          <button onClick={onClose} className="tiny-label ink-subtle hover:ink"><X size={16}/></button>
        </div>
        {children}
        <style>{`.i { margin-top: 6px; height: 42px; width: 100%; border-radius: 8px; background: hsl(var(--card)); border: 1px solid hsl(var(--border)); padding: 0 14px; color: hsl(var(--foreground)); font-size: 13px; }`}</style>
      </div>
    </div>
  );
}
function F({ label, children }) {
  return <label className="block mt-4"><div className="tiny-label">{label}</div>{children}</label>;
}
function SubmitRow({ onClose, label, testid, disabled }) {
  return (
    <div className="mt-6 flex gap-2">
      <button type="button" onClick={onClose} className="btn-pill btn-pill-outline h-11 flex-1">Cancel</button>
      <button type="submit" data-testid={testid} disabled={disabled} className="btn-pill btn-pill-solid h-11 flex-1 disabled:opacity-50"><Check size={13}/> {label}</button>
    </div>
  );
}
